/**
 * Announcements – learner and admin endpoints via the generated improve-api client.
 */
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {useImproveAxios} from "../../hooks/useImproveAxios";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
	type QueryClient
} from "@tanstack/react-query";
import {
	getImproveAPI,
	type GetLearnerAssignedResponse,
	type GetLearnerNotificationsResponse,
	type LearnerAssignedAnnouncementDto,
	type NotificationItemDto,
	type UserControllerGetAnnouncementsAssignedParams,
	type UserControllerGetAnnouncementsNotificationsParams,
	type AnnouncementsControllerGetListParams,
	type ListCohortAnnouncementsResponse,
	type AdminAnnouncementListItemDto,
	type GetAnnouncementResponseDto,
	type CreateAnnouncementBody,
	type UpdateAnnouncementBody,
	type GetAnnouncementEnrollmentsResponse,
	type AnnouncementsControllerGetEnrollmentsParams
} from "../gen";

/** API caps enrollments list page size (same rule as course cohort enrollments). */
export const ANNOUNCEMENT_ENROLLMENTS_MAX_PAGE_SIZE = 200;

export type UseGetAnnouncementEnrollmentsOptions =
  AnnouncementsControllerGetEnrollmentsParams & {
  	enabled?: boolean;
  };

export const ANNOUNCEMENTS_QUERY_KEYS = {
	LEARNER_ASSIGNED: "announcements-learner-assigned",
	LEARNER_NOTIFICATIONS: "announcements-learner-notifications",
	ADMIN_LIST: "announcements-admin-list",
	ADMIN_DETAIL: "announcements-admin-detail",
	learnerAssigned: (params?: UserControllerGetAnnouncementsAssignedParams) =>
		[ANNOUNCEMENTS_QUERY_KEYS.LEARNER_ASSIGNED, params] as const,
	learnerNotifications: (params?: UserControllerGetAnnouncementsNotificationsParams) =>
		[ANNOUNCEMENTS_QUERY_KEYS.LEARNER_NOTIFICATIONS, params] as const,
	adminList: (params?: AnnouncementsControllerGetListParams, filterModelSignature?: string) =>
		[ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST, params, filterModelSignature ?? ""] as const,
	adminDetail: (id: number) => [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_DETAIL, id] as const,
	ADMIN_ENROLLMENTS: "announcements-admin-enrollments",
	adminEnrollments: (id: number, params: AnnouncementsControllerGetEnrollmentsParams) =>
		["announcements-admin-enrollments", id, params] as const
} as const;

/** Prefer the largest valid saved position (API, React Query cache, sessionStorage, etc.). */
export const mergeAnnouncementVideoTimeSeconds = (
	...times: (number | null | undefined)[]
): number | undefined => {
	let max: number | undefined;
	for (const value of times) {
		if (value == null || !Number.isFinite(value) || value <= 0) {
			continue;
		}
		const rounded = Math.round(value);
		max = max == null ? rounded : Math.max(max, rounded);
	}
	return max;
};

/** Update learner-assigned query cache after WebSocket progress (before refetch returns videoTime). */
export const patchLearnerAssignedEnrollmentVideoTime = (
	queryClient: QueryClient,
	enrollmentId: number,
	videoTimeSeconds: number
): void => {
	const rounded = Math.round(videoTimeSeconds);
	queryClient.setQueriesData<GetLearnerAssignedResponse>(
		{queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_ASSIGNED]},
		(old) => {
			if (!old?.data) {
				return old;
			}
			return {
				...old,
				data: old.data.map((item) =>
					item.id === enrollmentId ? {...item, videoTime: rounded} : item
				)
			};
		}
	);
};

/** Keep notifications drawer items in sync with saved playback position. */
export const patchLearnerNotificationEnrollmentVideoTime = (
	queryClient: QueryClient,
	enrollmentId: number,
	videoTimeSeconds: number
): void => {
	const rounded = Math.round(videoTimeSeconds);
	queryClient.setQueriesData<GetLearnerNotificationsResponse>(
		{queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_NOTIFICATIONS]},
		(old) => {
			if (!old?.data) {
				return old;
			}
			return {
				...old,
				data: old.data.map((item) =>
					item.id === enrollmentId ? {...item, videoTime: rounded} : item
				)
			};
		}
	);
};

export type GetLearnerAssignedParams = UserControllerGetAnnouncementsAssignedParams;
export type GetLearnerNotificationsParams = UserControllerGetAnnouncementsNotificationsParams;
export type ListCohortAnnouncementsParams = AnnouncementsControllerGetListParams;
export type AdminAnnouncementListItem = AdminAnnouncementListItemDto;
export type GetAnnouncementResponse = GetAnnouncementResponseDto;

export type {
	GetLearnerAssignedResponse,
	GetLearnerNotificationsResponse,
	LearnerAssignedAnnouncementDto,
	ListCohortAnnouncementsResponse,
	CreateAnnouncementBody,
	UpdateAnnouncementBody
};
export type LearnerNotificationItemDto = NotificationItemDto;

/**
 * Get learner's assigned (incomplete) announcements for home page.
 * GET /api/_internal/user/announcements/assigned
 */
export const useGetLearnerAssignedAnnouncements = (
	params?: GetLearnerAssignedParams,
	options?: { enabled?: boolean }
) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetAnnouncementsAssigned} = getImproveAPI();
	return useQuery({
		queryKey: ANNOUNCEMENTS_QUERY_KEYS.learnerAssigned(params),
		queryFn: async () => {
			const timezone =
        params?.timezone ??
        encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
			const response = await userControllerGetAnnouncementsAssigned(
				{...params, timezone},
				axiosConfig
			);
			const payload = response.data;
			const publishedTime = (s?: string | null) => {
				if (!s) return 0;
				const t = new Date(s).getTime();
				return Number.isFinite(t) ? t : 0;
			};
			const sortedData = [...(payload.data ?? [])].sort(
				(a, b) => publishedTime(b.publishedAt) - publishedTime(a.publishedAt)
			);
			return {...payload, data: sortedData};
		},
		staleTime: 60 * 1000,
		refetchInterval: 60 * 1000,
		enabled: options?.enabled !== false
	});
};

/**
 * Get combined notifications + announcement enrollments (for notifications drawer).
 * GET /api/_internal/user/announcements/notifications
 */
export const useGetLearnerAnnouncementsNotifications = (
	params?: GetLearnerNotificationsParams,
	options?: {
		enabled?: boolean;
		/** When true, disables cache (staleTime and gcTime 0). Use for drawer so data is always fresh on open. */
		noCache?: boolean;
	}
) => {
	const axiosConfig = useImproveAxios();
	const {userControllerGetAnnouncementsNotifications} = getImproveAPI();
	return useQuery({
		queryKey: ANNOUNCEMENTS_QUERY_KEYS.learnerNotifications(params),
		queryFn: async () => {
			const response = await userControllerGetAnnouncementsNotifications(
				params,
				axiosConfig
			);
			return response.data;
		},
		staleTime: options?.noCache ? 0 : 60 * 1000,
		gcTime: options?.noCache ? 0 : undefined,
		enabled: options?.enabled !== false
	});
};

/**
 * Mark announcement as complete for current user.
 * PUT /api/_internal/user/announcements/:announcementSlug/complete
 */
export const useCompleteAnnouncement = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();
	const {userControllerCompleteAnnouncement} = getImproveAPI();
	return useMutation({
		mutationFn: async (announcementSlug: string) => {
			await userControllerCompleteAnnouncement(announcementSlug, axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_ASSIGNED]});
			queryClient.invalidateQueries({
				queryKey: [ANNOUNCEMENTS_QUERY_KEYS.LEARNER_NOTIFICATIONS]
			});
			successSnackbar("Announcement marked as seen");
		},
		onError: (error) => {
			errorSnackbar(error.message);
		}
	});
};

/**
 * List cohort announcements (admin/manager).
 * GET /api/_internal/announcements
 */
export const useListCohortAnnouncements = (
	params?: ListCohortAnnouncementsParams,
	options?: { enabled?: boolean; /** Distinguishes filter UI when API params are unchanged (e.g. contains vs equals). Not sent to the API. */ filterModelSignature?: string }
) => {
	const axiosConfig = useImproveAxios();
	const {announcementsControllerGetList} = getImproveAPI();
	return useQuery({
		queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminList(params, options?.filterModelSignature),
		queryFn: async () => {
			const response = await announcementsControllerGetList(params, axiosConfig);
			return response.data;
		},
		staleTime: 60 * 1000,
		enabled: options?.enabled !== false,
		// Keeps prior rows while page/sort/filter params change so the DataGrid stays mounted;
		// otherwise MUI X can crash on pagination (scrollToIndexes with undefined window ref).
		placeholderData: keepPreviousData
	});
};

/**
 * Get one announcement (admin). GET /api/_internal/announcements/:id
 */
export const useGetAnnouncement = (id: number | undefined, options?: { enabled?: boolean }) => {
	const axiosConfig = useImproveAxios();
	const {announcementsControllerGetOne} = getImproveAPI();
	return useQuery({
		queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(id!),
		queryFn: async () => {
			const response = await announcementsControllerGetOne(id!, axiosConfig);
			return response.data;
		},
		enabled: options?.enabled !== false && id != null,
		staleTime: 60 * 1000
	});
};

/**
 * Create announcement (admin). POST /api/_internal/announcements
 */
export const useCreateAnnouncement = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {announcementsControllerCreate} = getImproveAPI();
	return useMutation({
		mutationFn: async (body: CreateAnnouncementBody) => {
			const response = await announcementsControllerCreate(body, axiosConfig);
			return response.data as { id: number };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
		}
	});
};

/**
 * Update announcement (admin). PUT /api/_internal/announcements/:id
 */
export const useUpdateAnnouncement = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {announcementsControllerUpdate} = getImproveAPI();
	return useMutation({
		mutationFn: async ({id, body}: { id: number; body: UpdateAnnouncementBody }) => {
			await announcementsControllerUpdate(id, body, axiosConfig);
		},
		onSuccess: (_, {id}) => {
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
			queryClient.invalidateQueries({queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(id)});
		}
	});
};

/**
 * Delete announcement (admin). DELETE /api/_internal/announcements/:id
 */
export const useDeleteAnnouncement = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {announcementsControllerDelete} = getImproveAPI();
	return useMutation({
		mutationFn: async (id: number) => {
			await announcementsControllerDelete(id, axiosConfig);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
		}
	});
};

/**
 * Publish announcement. PUT /api/_internal/announcements/:id/publish
 */
export const usePublishAnnouncement = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {announcementsControllerPublish} = getImproveAPI();
	return useMutation({
		mutationFn: async (id: number) => {
			await announcementsControllerPublish(id, axiosConfig);
		},
		onSuccess: (_data, id) => {
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
			queryClient.invalidateQueries({queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(id)});
		}
	});
};

/**
 * Set announcement to draft. PUT /api/_internal/announcements/:id/draft
 */
export const useDraftAnnouncement = () => {
	const queryClient = useQueryClient();
	const axiosConfig = useImproveAxios();
	const {announcementsControllerDraft} = getImproveAPI();
	return useMutation({
		mutationFn: async (id: number) => {
			await announcementsControllerDraft(id, axiosConfig);
		},
		onSuccess: (_data, id) => {
			queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
			queryClient.invalidateQueries({queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(id)});
		}
	});
};

/**
 * List enrolled users for an announcement. Pass `complete` to restrict to seen/unseen; omit for all.
 * GET /api/_internal/announcements/:id/enrollments
 */
export const useGetAnnouncementEnrollments = (
	announcementId: number | undefined,
	options: UseGetAnnouncementEnrollmentsOptions
) => {
	const axiosConfig = useImproveAxios();
	const {announcementsControllerGetEnrollments} = getImproveAPI();
	const {enabled, page: pageOpt, pageSize: pageSizeOpt, ...rest} = options;

	const params: AnnouncementsControllerGetEnrollmentsParams = {
		...rest,
		page: pageOpt ?? 1,
		pageSize: pageSizeOpt ?? 25
	};

	return useQuery<GetAnnouncementEnrollmentsResponse>({
		queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminEnrollments(announcementId!, params),
		queryFn: async () => {
			const response = await announcementsControllerGetEnrollments(
				announcementId!,
				params,
				axiosConfig
			);
			return response.data;
		},
		enabled: enabled !== false && announcementId != null,
		staleTime: 60 * 1000,
		placeholderData: keepPreviousData
	});
};
